import { formatPublisherPaymentNature } from "components/Payment/DrawerContent/PublisherPaymentDetails";
import type { TModalActionButton } from "components/shared/ModalActionButtons";
import ModalActionButtons from "components/shared/ModalActionButtons";
import StyledButton from "components/shared/StyledButton";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { IPaymentInfo } from "store/paymentSlice";
import { EPaymentResourceType, removeTypeFromPayment } from "store/paymentSlice";
import { useAppDispatch } from "store/store";

import styles from "./style.module.scss";

interface IDeletePaymentType {
    payment: IPaymentInfo | undefined;
    beneficiaryName: string;
    paymentLotId: number | undefined | null;
    handleClose: (boolean) => void;
    paymentsLength: number;
}

/**
 * Modal in payment details for one beneficiary that allows
 * us to delete a payment type.
 */
const DeletePaymentType = ({
    payment,
    beneficiaryName,
    paymentLotId,
    handleClose,
    paymentsLength,
}: IDeletePaymentType) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [pending, setPending] = useState(false);

    const paymentType = () => {
        if (!payment) return "";
        return formatPublisherPaymentNature(payment, t);
    };

    const formatPaymentType = () => {
        if (!payment) return "";
        switch (payment.name) {
            case "partial":
                return EPaymentResourceType.PARTIAL_PAYMENT;
            case "term":
                return EPaymentResourceType.BENEFICIARY;
            case "advance":
                return EPaymentResourceType.ADVANCE_PAYMENT;
        }
    };

    const handleDeletePaymentType = async () => {
        if (!payment || !paymentLotId) return;
        setPending(true);
        const response = await dispatch(
            removeTypeFromPayment({
                hasOnlyOnePayment: paymentsLength === 1,
                paymentLotId,
                beneficiaryId: payment?.beneficiarId,
                paymentType: formatPaymentType(),
                periodId: payment.periodId,
            }),
        );

        if (response.meta.requestStatus === "fulfilled") {
            setPending(false);
            handleClose(paymentsLength === 1);
        } else {
            setPending(false);
        }
    };

    const buttonsDetails: TModalActionButton = {
        cancel: { label: t("ModalBase.Cancel"), action: () => handleClose(false) },
        confirm: { label: t("ModalBase.Confirm"), action: () => handleDeletePaymentType() },
    };

    return (
        <div className={styles["delete-payment-container"]}>
            <span>{t("PartialPayment.DeletePaymentTypeWarning", { name: beneficiaryName, type: paymentType() })}</span>
            <ModalActionButtons loading={pending} buttonsDetails={buttonsDetails} />
        </div>
    );
};

export default DeletePaymentType;
